/**
 * video-recorder.js
 * Trình ghi hình Video trực tiếp từ Three.js Canvas và Web Audio API (MediaRecorder).
 * Hỗ trợ xuất file video hoàn chỉnh (.webm / .mp4) để tải về máy tính.
 */

export class VideoRecorder {
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {AudioContext} audioCtx
   */
  constructor(canvas, audioCtx = null) {
    this.canvas = canvas;
    this.audioCtx = audioCtx;
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.isRecording = false;
    this.onStateChangeCallbacks = [];
  }

  onStateChange(cb) {
    if (typeof cb === 'function') this.onStateChangeCallbacks.push(cb);
  }

  notifyState(state, info = {}) {
    this.onStateChangeCallbacks.forEach((cb) => {
      try {
        cb({ state, isRecording: this.isRecording, ...info });
      } catch {}
    });
  }

  /**
   * Bắt đầu ghi hình Canvas & Âm thanh
   * @param {number} fps
   * @returns {boolean}
   */
  start(fps = 30) {
    if (this.isRecording) return false;
    this.recordedChunks = [];

    try {
      // 1. Lấy video stream từ Three.js Canvas
      const canvasStream = this.canvas.captureStream(fps);
      const combinedTracks = [...canvasStream.getVideoTracks()];

      // 2. Lấy audio track từ Web Audio nếu có
      if (this.audioCtx && this.audioCtx.createMediaStreamDestination) {
        try {
          const dest = this.audioCtx.createMediaStreamDestination();
          // Kết nối master audio vào destination
          if (dest.stream.getAudioTracks().length > 0) {
            combinedTracks.push(...dest.stream.getAudioTracks());
          }
        } catch (e) {
          console.warn('Không thể thu âm thanh vào video stream:', e);
        }
      }

      const combinedStream = new MediaStream(combinedTracks);

      // 3. Chọn định dạng hỗ trợ tốt nhất
      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/mp4';
      }

      this.mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : undefined,
        videoBitsPerSecond: 4500000 // 4.5 Mbps chất lượng cao
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.isRecording = false;
        this.saveRecording();
        this.notifyState('stopped');
      };

      this.mediaRecorder.start(200); // chunk mỗi 200ms
      this.isRecording = true;
      this.notifyState('recording');
      return true;
    } catch (err) {
      console.error('Không thể khởi động MediaRecorder:', err);
      this.notifyState('error', { error: err.message });
      return false;
    }
  }

  /**
   * Dừng ghi hình và lưu video
   */
  stop() {
    if (!this.isRecording || !this.mediaRecorder) return;
    try {
      this.mediaRecorder.stop();
    } catch (e) {
      console.warn('Lỗi khi dừng MediaRecorder:', e);
    }
  }

  /**
   * Đóng gói blob và kích hoạt tải về file video
   */
  saveRecording(fileName = null) {
    if (!this.recordedChunks.length) return;

    const mimeType = this.mediaRecorder?.mimeType || 'video/webm';
    const blob = new Blob(this.recordedChunks, { type: mimeType });
    const isMp4 = mimeType.includes('mp4');
    const ext = isMp4 ? 'mp4' : 'webm';
    const name = fileName || `anime_clip_${Date.now()}.${ext}`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
